import React, { useMemo, useState } from 'react';
import { Button, Dropdown, Form, Input, Label, Message, Modal, Pagination } from 'semantic-ui-react';
import { useNavigate } from 'react-router';

import { StyledAddButton } from 'common/components/button/StyledButton';
import { HeaderCell, Table, TableBody, TableCell, TableHeader, TableRow } from 'common/components/Table';
import LoadingSpinner from 'common/components/LoadingSpinner';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';

import {
  AssessmentQuestionSet,
  useCreateQuestionSetMutation,
  useListQuestionSetsQuery,
  usePatchQuestionSetMutation,
} from '../api/pulseSurveyApi';

const PAGE_SIZE = 10;
const GLOBAL_SCOPE = 3;

const PulseSurveyManagement: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('active');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState({
    title: '',
  });

  const { data: questionSets = [], isLoading, error, refetch } = useListQuestionSetsQuery({
    type: 1,
    scope: GLOBAL_SCOPE,
    include_archived: true,
  });

  const [createQuestionSet, { isLoading: creating }] = useCreateQuestionSetMutation();
  const [patchQuestionSet] = usePatchQuestionSetMutation();

  const filtered = useMemo(() => {
    let rows = questionSets.filter((row) => row.scope === GLOBAL_SCOPE);

    if (statusFilter === 'active') {
      rows = rows.filter((row) => !row.is_archived);
    } else if (statusFilter === 'archived') {
      rows = rows.filter((row) => row.is_archived);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((row) => row.title?.toLowerCase().includes(q));
    }

    rows.sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }));
    return rows;
  }, [questionSets, statusFilter, search]);

  const paged = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const onArchiveToggle = async (row: AssessmentQuestionSet, nextArchived: boolean, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (!row.id) return;
    await patchQuestionSet({ id: row.id, payload: { is_archived: nextArchived } }).unwrap();
    refetch();
  };

  const resetCreateModal = () => {
    setCreateError(null);
    setCreateForm({
      title: '',
    });
  };

  const onOpenCreate = () => {
    resetCreateModal();
    setIsCreateOpen(true);
  };

  const onCloseCreate = () => {
    setIsCreateOpen(false);
    resetCreateModal();
  };

  const onCreateQuestionSet = async () => {
    if (!createForm.title.trim()) {
      setCreateError('Title is required.');
      return;
    }

    setCreateError(null);

    try {
      const created = await createQuestionSet({
        payload: {
          title: createForm.title.trim(),
          type: 1,
          scope: GLOBAL_SCOPE,
          team: null,
          questions: [],
        },
      }).unwrap();

      onCloseCreate();
      refetch();
      if (created.id) {
        navigate(`/settings/admin/pulse-surveys/${created.id}/edit`);
      }
    } catch {
      setCreateError('Failed to create question set.');
    }
  };

  return (
    <PageContent>
      <PageSection>
        <PageHeader
          title="Pulse Questions"
          divider
          controls={
            <StyledAddButton
              label="New Question Set"
              width="180px"
              height="34px"
              onClick={onOpenCreate}
            />
          }
        />

        <PageSection display="flex" justifyContent="space-between" alignItems="center">
          <PageSection>
            <Input
              placeholder="Search by title"
              value={search}
              onChange={(_, { value }) => {
                setSearch(value);
                setCurrentPage(1);
              }}
              style={{ height: '36px', minWidth: '260px' }}
            />
          </PageSection>

          <Dropdown
            placeholder="Filter by Status"
            selection
            value={statusFilter}
            options={[
              { key: 'all', text: 'All Statuses', value: 'all' },
              { key: 'active', text: 'Active', value: 'active' },
              { key: 'archived', text: 'Archived', value: 'archived' },
            ]}
            onChange={(_, { value }) => {
              setStatusFilter(value as typeof statusFilter);
              setCurrentPage(1);
            }}
            style={{
              height: '36px',
              border: '1px solid #ccc',
              borderRadius: '5px',
              fontSize: '14px',
              minWidth: '200px',
              marginLeft: '12px',
            }}
          />
        </PageSection>

        {error && <Message negative content="Failed to load pulse survey question sets." />}

        {isLoading ? (
          <LoadingSpinner />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <HeaderCell width={6}>Title</HeaderCell>
                  <HeaderCell width={2}>Questions</HeaderCell>
                  <HeaderCell width={2}>Status</HeaderCell>
                  <HeaderCell width={2} justify="center">Actions</HeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paged.map((row) => (
                  <TableRow
                    key={row.id}
                    $selectable
                    onClick={() => navigate(`/settings/admin/pulse-surveys/${row.id}/edit`)}
                  >
                    <TableCell $firstcol>{row.title}</TableCell>
                    <TableCell>{row.questions?.length ?? 0}</TableCell>
                    <TableCell>
                      <Label color={row.is_archived ? 'red' : 'green'}>{row.is_archived ? 'Archived' : 'Active'}</Label>
                    </TableCell>
                    <TableCell justify="center">
                      <Dropdown
                        button
                        icon="ellipsis vertical"
                        floating
                        className="icon"
                        direction="left"
                        style={{ width: '38px', height: '35px', borderRadius: '8px' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Dropdown.Menu>
                          <Dropdown.Item
                            icon="edit"
                            text="Edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/settings/admin/pulse-surveys/${row.id}/edit`);
                            }}
                          />
                          {row.is_archived ? (
                            <Dropdown.Item
                              icon="undo"
                              text="Unarchive"
                              onClick={(e) => onArchiveToggle(row, false, e)}
                            />
                          ) : (
                            <Dropdown.Item
                              icon="archive"
                              text="Archive"
                              onClick={(e) => onArchiveToggle(row, true, e)}
                            />
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <PageSection display="flex" justifyContent="end">
              <Pagination
                activePage={currentPage}
                totalPages={totalPages}
                onPageChange={(_, data) => setCurrentPage(data.activePage as number)}
                firstItem={null}
                lastItem={null}
                prevItem={{ content: '‹', icon: true }}
                nextItem={{ content: '›', icon: true }}
              />
            </PageSection>
          </>
        )}
      </PageSection>

      <Modal open={isCreateOpen} onClose={onCloseCreate} size="tiny">
        <Modal.Header>Create Question Set</Modal.Header>
        <Modal.Content>
          <Form>
            <Form.Input
              label="Title"
              value={createForm.title}
              onChange={(_, { value }) => setCreateForm((prev) => ({ ...prev, title: String(value || '') }))}
              placeholder="Question set title"
              required
            />

            {createError && <Message negative content={createError} />}
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={onCloseCreate} disabled={creating}>Cancel</Button>
          <Button primary onClick={onCreateQuestionSet} loading={creating}>
            Create and Continue
          </Button>
        </Modal.Actions>
      </Modal>
    </PageContent>
  );
};

export default PulseSurveyManagement;
